import { createSlice } from '@reduxjs/toolkit';
import {
    fetchUserProfilePackageAssignments,
    fetchUserProfilePackageAssignmentsWithLicenseInfo,
    FormattedInheritedPPA,
    FormattedInheritedPPAWithLicenseInfo,
} from 'common/services/api/ProductPackages/PackageAssignments.thunk';
import { createMemoizedKeySelector } from 'common/utils/selectorUtils';

interface UserProfilePPAsSliceState {
    ppasByTenantId: Record<string, FormattedInheritedPPA[]>;
    ppasWithLicenseByTenantId: Record<string, FormattedInheritedPPAWithLicenseInfo[]>;
    processing: string | false;
    processingWithLicense: string | false;
}

type StateWithUserProfilePPAs = { userProfileProductPackageAssignments: UserProfilePPAsSliceState };

const initialState: UserProfilePPAsSliceState = {
    ppasByTenantId: {},
    ppasWithLicenseByTenantId: {},
    processing: false,
    processingWithLicense: false,
};

const userProfileProductPackageAssignmentsSlice = createSlice({
    name: 'userProfileProductPackageAssignments',
    initialState,

    reducers: (create) => ({
        clearUserProfilePPAs: create.reducer(state => {
            state.ppasByTenantId = {};
            state.ppasWithLicenseByTenantId = {};
        }),
    }),

    extraReducers: builder => {
        builder.addCase(fetchUserProfilePackageAssignments.pending, (state, args) => {
            state.processing = args.meta.arg.tenantId;
            state.ppasByTenantId[args.meta.arg.tenantId] = [];
        });
        builder.addCase(fetchUserProfilePackageAssignments.fulfilled, (state, action) => {
            state.processing = false;
            if (action.payload.length) {
                const tenantId = action.meta.arg.tenantId;
                state.ppasByTenantId = {
                    ...state.ppasByTenantId,
                    [tenantId]: action.payload,
                };
            }
        });
        builder.addCase(fetchUserProfilePackageAssignments.rejected, (state, args) => {
            state.processing = false;
            state.ppasByTenantId[args.meta.arg.tenantId] = [];
        });

        builder.addCase(fetchUserProfilePackageAssignmentsWithLicenseInfo.pending, (state, args) => {
            const tenantId = args.meta.arg.currentUserProfileTenant.id;
            state.processingWithLicense = tenantId;
            state.ppasWithLicenseByTenantId[tenantId] = [];
        });
        builder.addCase(fetchUserProfilePackageAssignmentsWithLicenseInfo.fulfilled, (state, action) => {
            state.processingWithLicense = false;
            if (action.payload.length) {
                const tenantId = action.meta.arg.currentUserProfileTenant.id;
                state.ppasWithLicenseByTenantId = {
                    ...state.ppasWithLicenseByTenantId,
                    [tenantId]: action.payload,
                };
            }
        });
        builder.addCase(fetchUserProfilePackageAssignmentsWithLicenseInfo.rejected, (state, args) => {
            state.processingWithLicense = false;
            state.ppasWithLicenseByTenantId[args.meta.arg.currentUserProfileTenant.id] = [];
        });

    }
});

// eslint-disable-next-line no-restricted-syntax
export default userProfileProductPackageAssignmentsSlice.reducer;
export const { clearUserProfilePPAs } = userProfileProductPackageAssignmentsSlice.actions;

const selectPPAsByTenantIdState = (state: StateWithUserProfilePPAs) => state.userProfileProductPackageAssignments.ppasByTenantId;
const selectPPAsWithLicenseByTenantIdState = (state: StateWithUserProfilePPAs) => state.userProfileProductPackageAssignments.ppasWithLicenseByTenantId;

export const selectUserProfilePPAsByTenantId = createMemoizedKeySelector(selectPPAsByTenantIdState);
export const selectUserProfilePPAsWithLicenseByTenantId = createMemoizedKeySelector(selectPPAsWithLicenseByTenantIdState);
export const selectProcessingPPAs = (state: StateWithUserProfilePPAs) => state.userProfileProductPackageAssignments.processing;
export const selectProcessingPPAsWithLicense = (state: StateWithUserProfilePPAs) => state.userProfileProductPackageAssignments.processingWithLicense;
